import React, { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TelemetryEventName } from '@little-chef/domain';
import { initAnalytics } from './services/telemetry/analyticsInit';
import { initSentry } from './services/telemetry/sentryInit';
import { getTelemetryService } from './services/telemetry/telemetryService';
import { getConsentService } from './services/telemetry/granularConsentService';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ToastProvider } from './components/common/Toast';
import { useServiceWorker } from './hooks/useServiceWorker';
import { UpdateBanner } from './components/UpdateBanner';
import { CookiesRequired, areCookiesEnabled } from './components/CookiesRequired';
import { ProfileSetupGate } from './components/common/ProfileSetupGate';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { CookieConsentProvider } from './features/cookies/CookieConsentProvider';
import { FeatureFlagsProvider } from './features/featureFlags/context/FeatureFlagsContext';
import { authRoutes } from './routes/authRoutes';
import { setupRoutes } from './routes/setupRoutes';
import { mainRoutes } from './routes/mainRoutes';
import { wizardRoutes } from './routes/wizardRoutes';
import { initI18n } from './i18n';
import './index.css';

const NotFoundPage = React.lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

// 1. Init analytics first (analytics instance must exist before Sentry's beforeSend can use it)
const analytics = initAnalytics();
// 2. Gate analytics SDK immediately based on stored consent (prevents pre-consent events).
//    GranularConsentService constructor calls setAnalyticsCollectionEnabled safely in try/catch.
getConsentService(analytics);
// 3. Init Sentry second (beforeSend cross-link needs analytics)
initSentry(analytics);
// 4. Initialise the telemetry service singleton, flush buffered events, and fire session_start
const telemetryService = getTelemetryService(analytics);
telemetryService.flushBuffer();
telemetryService.trackEvent(TelemetryEventName.SESSION_START, {});

/** Tracks screen_view on every route change. Mounted once inside BrowserRouter. */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    telemetryService.trackScreenView(location.pathname);
  }, [location.pathname]);
  return null;
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// App component to render inside providers
function App() {
  const { needsRefresh, updateSW } = useServiceWorker();

  if (!areCookiesEnabled()) {
    return <CookiesRequired />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <UpdateBanner needsRefresh={needsRefresh} updateSW={updateSW} />
          <ToastProvider>
            <CookieConsentProvider>
              <RouteTracker />
              <AuthProvider>
                <FeatureFlagsProvider>
                  <Suspense
                    fallback={
                      <div className="min-h-screen flex items-center justify-center bg-[color:var(--color-background)]">
                        <LoadingSpinner />
                      </div>
                    }
                  >
                    <Routes>
                      {/* Public auth routes */}
                      {authRoutes()}

                      {/* Protected routes (require authentication) */}
                      <Route element={<ProtectedRoute />}>
                        {/* Post-login setup — no username yet */}
                        {setupRoutes()}

                        {/* Profile-setup-gated routes (require terms accepted + username) */}
                        <Route element={<ProfileSetupGate />}>
                          {mainRoutes()}
                          {wizardRoutes()}
                        </Route>
                      </Route>

                      <Route path="/" element={<Navigate to="/catalog" replace />} />

                      {/* Catch-all: renders for any URL that does not match a defined route */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </FeatureFlagsProvider>
              </AuthProvider>
            </CookieConsentProvider>
          </ToastProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Bootstrap: load all translations first, then render to prevent flash of keys
async function bootstrap() {
  await initI18n();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

bootstrap().catch((error) => {
  console.error('Failed to bootstrap app:', error);
});
